import type {
  ImportHeaderAliasesResponse,
  ProviderExportHeaders,
  SourcePlatform,
} from '~entities/import-job/api/types';

/** Studio Pro is the DSP product rename; keep `dsp` for legacy jobs. */
export function isStudioProPlatform(platform: SourcePlatform): boolean {
  return platform === 'studio_pro' || platform === 'dsp';
}

/** Studio Pro / DSP export headers (same product). */
const STUDIO_PRO_EXPORT_HEADERS: ProviderExportHeaders = {
  classes: [
    'ClassName',
    'Class Name',
    'ClassStyle',
    'Teacher',
    'ClassDays',
    'StartTime',
    'EndTime',
    'StartDate',
    'EndDate',
    'Studio',
    'Room',
    'MonthlyTuition',
    'RegFee',
    'MinAge',
    'MaxAge',
    'ClassSize',
  ],
  families: [
    'Student Name',
    'Parent',
    'Parent Phone',
    'Parent Email',
    'Student',
    'Student Email',
    'ParentFirst',
    'ParentLast',
    'Email1',
    'CellPhone',
    'StudentFirst',
    'StudentLast',
    'DOB',
  ],
};

const STUDIO_PRO_CLASS_ALIASES: Record<string, string> = {
  ClassName: 'Title',
  'Class Name': 'Title',
  ClassStyle: 'Class Color',
  Teacher: 'Instructor',
  ClassDays: 'Days',
  StartTime: 'Start Time',
  EndTime: 'End Time',
  StartDate: 'Start Date',
  EndDate: 'End Date',
  Studio: 'Location',
  Room: 'Room',
  MonthlyTuition: 'Tuition Fee',
  RegFee: 'Registration Fee',
  MinAge: 'start age year',
  MaxAge: 'end age year',
  ClassSize: 'Class Size',
};

const STUDIO_PRO_FAMILY_ALIASES: Record<string, string> = {
  Parent: 'Parent First Name',
  'Parent Phone': 'Parent Primary Phone',
  'Parent Email': 'Parent Email',
  'Student Name': 'Student 1 First Name',
  'Student Email': 'Student 1 Email',
  Student: 'Student 1 Email',
  ParentFirst: 'Parent First Name',
  ParentLast: 'Parent Last Name',
  Email1: 'Parent Email',
  CellPhone: 'Parent Primary Phone',
  StudentFirst: 'Student 1 First Name',
  StudentLast: 'Student 1 Last Name',
  DOB: 'Student 1 Date of Birth',
};

/** Studio Director class auto-map. Headers include trailing " x". Length x → End Time via transform. */
const STUDIO_DIRECTOR_CLASS_ALIASES: Record<string, string> = {
  'Name x': 'Title',
  'Location x': 'Location',
  'Instructor x': 'Instructor',
  'Season x': 'Session',
  'Day x': 'Days',
  'Start Time x': 'Start Time',
};

/** Real Studio Director family/guardian export headers. */
const STUDIO_DIRECTOR_FAMILY_ALIASES: Record<string, string> = {
  Guardian: 'Parent First Name',
  'Family Last Name': 'Parent Last Name',
  Email: 'Parent Email',
  Phone1: 'Parent Primary Phone',
  Address1: 'Address Street',
  Address2: 'Address Apartment',
  City: 'Address City',
  State: 'Address State',
  ZIP: 'Address Zip',
  'Student 1 Name': 'Student 1 First Name',
  'Student 1 DOB': 'Student 1 Date of Birth',
  'Student 1 Gender': 'Student 1 Gender',
  'Student 2 Name': 'Student 2 First Name',
  'Student 2 DOB': 'Student 2 Date of Birth',
  'Student 2 Gender': 'Student 2 Gender',
  'Student 3 Name': 'Student 3 First Name',
  'Student 3 DOB': 'Student 3 Date of Birth',
  'Student 3 Gender': 'Student 3 Gender',
  'Student 4 Name': 'Student 4 First Name',
  'Student 4 DOB': 'Student 4 Date of Birth',
  'Student 4 Gender': 'Student 4 Gender',
  'Student 5 Name': 'Student 5 First Name',
  'Student 5 DOB': 'Student 5 Date of Birth',
  'Student 5 Gender': 'Student 5 Gender',
};

const STUDIO_DIRECTOR_EXPORT_HEADERS: ProviderExportHeaders = {
  classes: [
    'Season x',
    'Name x',
    'Day x',
    'Start Time x',
    'Length x',
    'Instructor x',
    'Location x',
  ],
  families: [
    'Guardian',
    'Family Last Name',
    'Email',
    'Phone1',
    'Address1',
    'City',
    'State',
    'ZIP',
    'Student 1 Name',
    'Student 1 DOB',
    'Student 1 Gender',
  ],
};

/** Client fallback when /import-jobs/header-aliases is unavailable (local dev, stale BE). */
export const FALLBACK_PROVIDER_EXPORT_HEADERS: Record<
  SourcePlatform,
  ProviderExportHeaders
> = {
  jackrabbit: {
    classes: [
      'Days',
      'Time',
      'Duration',
      'Lesson Format',
      'Standard',
      'Teacher',
    ],
    families: [
      'First Name',
      'Last Name',
      'DOB',
      'Customer',
      'Customer Email',
      'Customer Email 2',
      'Customer Phone',
    ],
  },
  studio_pro: STUDIO_PRO_EXPORT_HEADERS,
  studio_director: STUDIO_DIRECTOR_EXPORT_HEADERS,
  dsp: STUDIO_PRO_EXPORT_HEADERS,
  csv: { classes: [], families: [] },
};

export const FALLBACK_IMPORT_HEADER_ALIASES: ImportHeaderAliasesResponse = {
  platforms: ['jackrabbit', 'studio_pro', 'studio_director', 'dsp', 'csv'],
  providerExportHeaders: FALLBACK_PROVIDER_EXPORT_HEADERS,
  classAliases: {
    jackrabbit: {
      'Lesson Format': 'Title',
      Standard: 'Group',
      Days: 'Days',
      Time: 'Start Time',
      Teacher: 'Instructor',
      'Class Name': 'Title',
      Category: 'Class Color',
      Staff: 'Instructor',
      Day: 'Days',
      'Start Time': 'Start Time',
      'End Time': 'End Time',
      'Start Date': 'Start Date',
      'End Date': 'End Date',
      Location: 'Room',
      Tuition: 'Tuition Fee',
      'Registration Fee': 'Registration Fee',
      'Min Age': 'start age year',
      'Max Age': 'end age year',
      'Max Students': 'Class Size',
    },
    studio_pro: STUDIO_PRO_CLASS_ALIASES,
    studio_director: STUDIO_DIRECTOR_CLASS_ALIASES,
    dsp: STUDIO_PRO_CLASS_ALIASES,
    csv: {},
  },
  familyAliases: {
    jackrabbit: {
      'First Name': 'Parent First Name',
      'Last Name': 'Parent Last Name',
      DOB: 'Student 1 Date of Birth',
      'Customer Email': 'Parent Email',
      'Customer Phone': 'Parent Primary Phone',
    },
    studio_pro: STUDIO_PRO_FAMILY_ALIASES,
    studio_director: STUDIO_DIRECTOR_FAMILY_ALIASES,
    dsp: STUDIO_PRO_FAMILY_ALIASES,
    csv: {},
  },
};

export function getFamilyAliasesForPlatform(
  sourcePlatform: SourcePlatform,
  apiData?: ImportHeaderAliasesResponse | null,
): Record<string, string> {
  const fallback =
    FALLBACK_IMPORT_HEADER_ALIASES.familyAliases[sourcePlatform] ?? {};
  const api = apiData?.familyAliases?.[sourcePlatform] ?? {};
  return { ...api, ...fallback };
}

export function getClassAliasesForPlatform(
  sourcePlatform: SourcePlatform,
  apiData?: ImportHeaderAliasesResponse | null,
): Record<string, string> {
  const fallback =
    FALLBACK_IMPORT_HEADER_ALIASES.classAliases[sourcePlatform] ?? {};
  const api = apiData?.classAliases?.[sourcePlatform] ?? {};
  return { ...api, ...fallback };
}

export function getProviderExportHeaders(
  sourcePlatform: SourcePlatform,
  apiData?: ImportHeaderAliasesResponse | null,
): ProviderExportHeaders {
  return (
    apiData?.providerExportHeaders?.[sourcePlatform] ??
    FALLBACK_PROVIDER_EXPORT_HEADERS[sourcePlatform] ?? {
      classes: [],
      families: [],
    }
  );
}

export function isJackrabbitFamilyExport(headers: Array<string>): boolean {
  const normalized = headers.map((h) => h.trim());
  return (
    normalized.includes('Account First Name') &&
    normalized.includes('Account Last Name') &&
    normalized.some((h) => /^Student 1 First Name$/i.test(h))
  );
}

export function isJackrabbitClassExport(headers: Array<string>): boolean {
  const normalized = headers.map((h) => h.trim());
  return (
    normalized.includes('Lesson Format') ||
    normalized.includes('Class Name') ||
    normalized.includes('Category')
  );
}
